import LegalToolTip from "@/components/LegalTerms/LegalToolTip";
import SearchableDropdown from "@/components/shared/SearchableDropdown";
import { countryList, isValidTin, setClickInsightFormErrors, trimOptionText } from "@/lib/util";
import {
  clearPolExposedState,
  clearTaxNotCanadaNotUsState,
  clearTaxResidentorCitizenUsState,
  setCountryPositionHeld,
  setCrsCountry,
  setCrsTin,
  setCrsUsTin,
  setAMLFieldsCompleted,
  setMeridianOnly,
  setNatureOfBanking,
  setOccupation,
  setOntarioWork,
  setOwnerOperator,
  setPolExposed,
  setPoliticallyExposedType,
  setPositionHeld,
  setPrimaryPurpose,
  setPrimaryOtherPurpose,
  setResStatus,
  setTaxNotCanadaNotUS,
  setTaxResidentorCitizenUs,
  setPrimaryPurposeGIC,
  setPrimaryOtherPurposeGIC,
} from "@/slices/legalTermsSlice";
import { RootState } from "@/store/dtm";
import style from "@/styles/pages/LegalTerms/LegalTerms.module.css";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isValidSSN } from "@/lib/util";
import { setFinalizeAccoutComplete } from "@/slices/progressSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto, EmploymentForManipulationDto, EmploymentIncomeDto, OtherIncome, RegulatoryComplainceDto, TokenData, UpdateEmploymentModel } from "@/lib/DTMWebAPIClient.generated";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setApplicationEndpointReached, setApplicationProgress } from "@/slices/applicationSlice";
import { useAuth } from "@/context/AuthContext";
import dayjs from "dayjs";

export default function LegalTerms() {
  const [canTaxResidentSelected, setCanTaxResidentSelected] = useState("");
  const [usTaxResidentSelected, setusTaxResidentSelected] = useState("");
  const [taxResidentOtherSelected, setTaxResidentOtherSelected] = useState("");
  const [pepPositionSelected, setPepPositionSelected] = useState("");
  const [occupationValue, setOccupationValue] = useState("");
  const [primaryPurposeValue, setPrimaryPurposeValue] = useState("");
  const [primaryPurposeGICValue, setPrimaryPurposeGICValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [occupationCategory, setOccupationCategory] = useState("");
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const sessionData = useSelector(
    (state: RootState) => state.sessionData.currentSession
  );
  const legalTerms = useSelector(
    (state: RootState) => state.legalTerms
  );
  const homeAddress = useSelector(
    (state: RootState) => state.homeAddress.address
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { login } = useAuth();


  const methods = useForm({
    defaultValues: {
      resStatus: legalTerms.legalTerms.fieldsCompleted
        ? legalTerms.legalTerms.resStatus ? "yes" : "no"
        : "",
      taxResidentorCitizenUs: "",
      taxNotCanadaNotUS: "",
      polExposed: "",
      ontarioWork: legalTerms.legalTerms.fieldsCompleted
        ? legalTerms.legalTerms.ontarioWork ? "yes" : "no"
        : "",
      ownerOperator: legalTerms.legalTerms.fieldsCompleted
        ? legalTerms.legalTerms.ownerOperator ? "yes" : "no"
        : "",
      meridianOnly: legalTerms.legalTerms.fieldsCompleted
        ? legalTerms.legalTerms.meridianOnly ? "yes" : "no"
        : "",
      occupation: legalTerms.legalTerms.occupation,
      natureOfBanking: legalTerms.legalTerms.natureOfBanking,
      primaryPurpose: legalTerms.legalTerms.primaryPurpose,
      primaryOtherPurpose: legalTerms.legalTerms.primaryOtherPurpose,
      primaryPurposeGIC: legalTerms.legalTerms.primaryPurposeGIC,
      primaryOtherPurposeGIC: legalTerms.legalTerms.primaryOtherPurposeGIC,
      crsUsTin: legalTerms.legalTerms.crsUsTin,
      crsTin: legalTerms.legalTerms.crsTin,
      crsCountry: legalTerms.legalTerms.crsCountry,
      politicallyExposedType: legalTerms.legalTerms.politicallyExposedType,
      positionHeld: legalTerms.legalTerms.positionHeld,
      countryPositionHeld: legalTerms.legalTerms.countryPositionHeld,
      residencyError: "",
      employmentError: ""
    },
  });

  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
  }

  const onSubmit = async (data: any) => {
    if (methods.getValues("resStatus") === "no" && methods.getValues("ontarioWork") === "no") {
      methods.setError("employmentError", {
        type: "employmentError",
        message: "You must be a Canadian Resident or be employed in Canada",
      });
      return;
    }

    dispatch(setResStatus(data.resStatus === "yes" ? true : false));
    dispatch(setTaxResidentorCitizenUs(data.taxResidentorCitizenUs === "yes" ? true : false));
    dispatch(setTaxNotCanadaNotUS(data.taxNotCanadaNotUS === "yes" ? true : false));
    dispatch(setPolExposed(data.polExposed === "yes" ? true : false));
    dispatch(setOntarioWork(data.ontarioWork === "yes" ? true : false));
    dispatch(setOwnerOperator(data.ownerOperator === "yes" ? true : false));
    dispatch(setMeridianOnly(data.meridianOnly === "yes" ? true : false));
    dispatch(setOccupation(data.occupation));
    dispatch(setNatureOfBanking(data.natureOfBanking));
    dispatch(setPrimaryPurpose(data.primaryPurpose));
    dispatch(setPrimaryOtherPurpose(data.primaryOtherPurpose));
    dispatch(setPrimaryPurposeGIC(data.primaryPurposeGIC));
    dispatch(setPrimaryOtherPurposeGIC(data.primaryOtherPurposeGIC));
    dispatch(setAMLFieldsCompleted(true));

    isUsTaxResidentSelected(data);
    isTaxResidentOtherSelected(data);
    isPepPositionSelected(data);

    setIsSaving(true);
    saveCompliance(data);
  };

  const saveCompliance = async (data: any) => {
    //Update compliance info on Trellis backend
    await client.apiApplicantsCompliance(new RegulatoryComplainceDto({
      fatcaReportingStatusType: data.taxResidentorCitizenUs === "yes" ? "Reportable" : "Not Reportable",
      fatcaTinType: "",
      fatcaTinNumber: data.crsUsTin ? data.crsUsTin : "",
      isUsCitizen: data.taxResidentorCitizenUs === "yes" ? true : false,
      isUsResident: data.taxResidentorCitizenUs === "yes" ? true : false,
      isCanadianTaxResident: data.resStatus === "yes" ? true : false,
      isTaxResidentOutsideCanUsa: data.taxNotCanadaNotUS === "yes" ? true : false,
      crsReportingStatusType: "",
      crsCountry: data.crsCountry,
      crsTinNumber: data.crsTin,
      noTinReasonType: "",
      reasonIfOther: "",
      crsCountry2: "",
      crsTinNumber2: "",
      noTinReasonType2: "",
      reasonIfOther2: "",
      liveInCanada: homeAddress.country.toLowerCase() == "canada" ? true : false,
      workInCanada: data.ontarioWork === "yes" ? true : false,
      financialRelationshipType: "With Meridian and other FI(s)",
      meridianRelationshipType: "Personal accounts only",
      isPoliticallyExposed: data.polExposed === "yes" ? true : false,
      positionOfOfficeHeld: "",
      nameOfGovernmentInstitution: "",
      governmentLevelType: "",
      countryPositionOfOfficeHeld: "",
      isFamilyMemberPoliticallyExposed: data.politicallyExposedType == "relative",
      exposedFamilyType: "",
      familyPositionOfOfficeHeld: "",
      familyNameOfGovernmentInstitution: "",
      familyGovernmentLevelType: "",
      familyCountryPositionOfOfficeHeld: "",
      ownerOperator: data.ownerOperator === "yes" ? true : false
    })).then((result) => {

      setIsSaving(false);
      dispatch(setApplicationProgress(16));
      if (application.applicationEndpointReached == false) {
        dispatch(setApplicationEndpointReached(true));
      }
      if (applicant.isProspect) {
        navigate("/verify-id/accept-terms");
      } else {
        navigate("/joint/confirm-application");
      }

    }).catch((error) => {

      setIsSaving(false);
      console.log("Error saving compliance information", error);
      dispatch(setApplicationProgress(16));
      if (application.applicationEndpointReached == false) {
        dispatch(setApplicationEndpointReached(true));
      }
      if (applicant.isProspect) {
        navigate("/verify-id/accept-terms");
      } else {
        navigate("/joint/confirm-application");
      }
    });
  }

  const checkResidency = () => {
    const resStatus = methods.watch("resStatus");
    const taxResidentorCitizenUs = methods.watch("taxResidentorCitizenUs");
    const taxNotCanadaNotUS = methods.watch("taxNotCanadaNotUS");

    if (
      resStatus === "no" &&
      taxResidentorCitizenUs === "no" &&
      taxNotCanadaNotUS === "no"
    ) {
      methods.setError("residencyError", {
        type: "residency",
        message: "At least one of the residency must be 'Yes'.",
      });
    } else {
      methods.clearErrors("residencyError");
      methods.clearErrors("employmentError");
    }
  };

  const taxResidentCanadaHandler = (e: any) => {
    setCanTaxResidentSelected(e.target.value);
    checkResidency();
  };
  const taxResidentorCitizenUsHandler = (e: any) => {
    setusTaxResidentSelected(e.target.value);

    if (e.target.value === "no") {
      methods.resetField("crsUsTin");
      methods.unregister("crsUsTin");
    }
    else {
      methods.register("crsUsTin", {
        required: 'TIN is required',
        validate: {
          ssnValidator: (value) => isValidSSN(value) == true,
        }
      });
    }
    checkResidency();
  };

  const taxResidentOtherHandler = (e: any) => {
    setTaxResidentOtherSelected(e.target.value);

    if (e.target.value === "no") {
      methods.resetField("crsTin");
      methods.resetField("crsCountry");
      methods.unregister(["crsTin", "crsCountry"]);
    } else {
      methods.register("crsTin", {
        required: "TIN is required",
      });
      methods.register("crsCountry", {
        required: "Select your country of tax residency",
      });
    }
    checkResidency();
  };

  const pepPositionHandler = (e: any) => {
    setPepPositionSelected(e.target.value);

    if (e.target.value === "no") {
      methods.unregister([
        "politicallyExposedType",
        "positionHeld",
        "countryPositionHeld",
      ]);
    } else {
      methods.register("politicallyExposedType", {
        required: "Select who is the politically exposed person",
      });
      methods.register("positionHeld", {
        required: "Select position or office held",
      });
      methods.register("countryPositionHeld", {
        required: "Select country of Position or Office Held",
      });
    }
  };

  const isUsTaxResidentSelected = (data: any) => {
    if (usTaxResidentSelected === "yes") {
      dispatch(setCrsUsTin(data.crsUsTin));
    } else {
      dispatch(clearTaxResidentorCitizenUsState());
    }
  };

  const isTaxResidentOtherSelected = (data: any) => {
    if (taxResidentOtherSelected === "yes") {
      dispatch(setCrsTin(data.crsTin));
      dispatch(setCrsCountry(data.crsCountry));
    } else {
      dispatch(clearTaxNotCanadaNotUsState());
    }
  };

  const isPepPositionSelected = (data: any) => {
    if (pepPositionSelected === "yes") {
      dispatch(setPoliticallyExposedType(data.politicallyExposedType));
      dispatch(setPositionHeld(data.positionHeld));
      dispatch(setCountryPositionHeld(data.countryPositionHeld));
    } else {
      dispatch(clearPolExposedState());
    }
  };

  const primaryPurposeValueHandler = (e: any) => {
    setPrimaryPurposeValue(e.target.value);

    if (e.target.value === "9") {
      methods.register("primaryOtherPurpose", {
        required: "Other purpose cannot be empty",
      });
    } else {
      methods.resetField("primaryOtherPurpose");
      methods.unregister("primaryOtherPurpose");
    }
  };

  const primaryPurposeGICValueHandler = (e: any) => {
    setPrimaryPurposeGICValue(e.target.value);

    if (e.target.value === "9") {
      methods.register("primaryOtherPurposeGIC", {
        required: "Other purpose cannot be empty",
      });
    } else {
      methods.resetField("primaryOtherPurposeGIC");
      methods.unregister("primaryOtherPurposeGIC");
    }
  };

  const getCountry = countryList(true);
  const getCountryInclNA = countryList(false);

  // const pepPositionList = () => {
  //   return application.pepPositions.map((position: any) => {
  //     const positionName = trimOptionText(position.name, 38);
  //     return (
  //       <>
  //         <option
  //           key={position.code}
  //           value={position.code}
  //           title={position.name}
  //         >
  //           {positionName}
  //         </option>
  //       </>
  //     );
  //   });
  // };

  useEffect(() => {
    document.title = "Review the legal terms";

    dispatch(setFinalizeAccoutComplete(true));

    if (legalTerms.legalTerms.fieldsCompleted) {
      setOccupationValue(legalTerms.legalTerms.occupation);
      let category = sessionData.occupations?.find((x) => x.name == legalTerms.legalTerms.occupation)?.category;
      setOccupationCategory(category ? category : "");

      setusTaxResidentSelected(legalTerms.legalTerms.taxResidentorCitizenUs ? "yes" : "no");
      setTaxResidentOtherSelected(legalTerms.legalTerms.taxNotCanadaNotUS ? "yes" : "no");
      setPepPositionSelected(legalTerms.legalTerms.polExposed ? "yes" : "no");
      setPrimaryPurposeValue(legalTerms.legalTerms.primaryPurpose);


      methods.setValue("resStatus", legalTerms.legalTerms.resStatus ? "yes" : "no");
      methods.setValue("taxResidentorCitizenUs", legalTerms.legalTerms.taxResidentorCitizenUs ? "yes" : "no");
      methods.setValue("taxNotCanadaNotUS", legalTerms.legalTerms.taxNotCanadaNotUS ? "yes" : "no");
      methods.setValue("primaryOtherPurpose", legalTerms.legalTerms.primaryOtherPurpose);
      methods.setValue("primaryOtherPurposeGIC", legalTerms.legalTerms.primaryOtherPurposeGIC);
    }

    //Refresh Token if it is expired
    if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
      client.apiAuthRefreshtoken(new TokenData({
        accessToken: credential.credential.accessToken,
        refreshToken: credential.credential.refreshToken
      })).then((result) => {
        login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
        updateAppData();
      })
    } else {
      updateAppData();
    }
  }, []);

  const updateAppData = () => {
    client.apiApplicationsFetch().then((result) => {
      result.application = application;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });
  }

  return (
    <SlideTransition>
      <div className="flex flex-col max-xs:px-3 sm:ml-72 max-md:items-center max-md:mt-20">
        <div className={style.ltHeading} role="region" aria-label="Legal Terms">
          <h1>Review the legal terms</h1>
          <p className="sub-heading">
            This is the stuff we're legally required to ask. Be sure to complete
            all the questions.
          </p>
          <div className="flex flex-row mt-5">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                <div>
                  <div
                    className={clsx(
                      "flex sm:grid-flow-col",
                      style.TRCanada
                    )}
                  >
                    <span className="mr-1">
                      Are you a tax resident of Canada?
                      <span>
                        {methods.formState?.errors?.resStatus?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.resStatus?.message}
                          </span>
                        )}
                        {methods.formState?.errors?.residencyError?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.residencyError?.message}
                          </span>
                        )}
                        {methods.formState?.errors?.employmentError?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.employmentError?.message}
                          </span>
                        )}
                      </span>
                    </span>

                    <span>
                      <LegalToolTip
                        title=""
                        content="We require this information for tax reporting
                              purposes. Generally, an individual will be a tax
                              resident of a country if, under the laws of that
                              country, they pay or should be paying tax there
                              because of their domicile. For more information
                              contact CRA."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="resStatusYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          {...methods.register("resStatus", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentCanadaHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="resStatusYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="resStatusNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          {...methods.register("resStatus", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentCanadaHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="resStatusNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  <div
                    className={clsx(
                      "flex mt-5",
                      style.TRUsa
                    )}
                  >
                    <span className="mr-1">
                      Are you a tax resident or a citizen of the United States?
                      <span>
                        {methods.formState?.errors?.taxResidentorCitizenUs
                          ?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                              {
                                methods.formState?.errors?.taxResidentorCitizenUs
                                  ?.message
                              }
                            </span>
                          )}
                        {methods.formState?.errors?.residencyError?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.residencyError?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span>
                      <LegalToolTip
                        title=""
                        content="In March 2010, the Foreign Account Tax Compliance
                              Act (FATCA) was signed into U.S. law. The purpose
                              of the Act is to identify individuals who may
                              evade US taxes by holding accounts outside of the
                              U.S. On February 5, 2014, Canada signed an
                              intergovernmental agreement with the U.S.
                              regarding FATCA. Under this agreement, Canada
                              agreed to pass laws requiring financial
                              institutions to report annually (in some cases) to
                              the Canada Revenue Agency (CRA) on specified
                              accounts held in Canada by U.S. persons. The CRA
                              may forward this reporting to the IRS under the
                              provisions and safeguards of the Canada-U.S. Tax
                              Convention."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="taxResidentorCitizenUsYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          checked={usTaxResidentSelected === "yes"}
                          {...methods.register("taxResidentorCitizenUs", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentorCitizenUsHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="taxResidentorCitizenUsYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="taxResidentorCitizenUsNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          checked={usTaxResidentSelected === "no"}
                          {...methods.register("taxResidentorCitizenUs", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentorCitizenUsHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="taxResidentorCitizenUsNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>

                  {usTaxResidentSelected == "yes" && (
                    <div className="mt-5">
                      <div
                        className={clsx(
                          "flex mt-5",
                          style.Tin
                        )}
                      >
                        <span className="mr-1">TIN</span>
                        <LegalToolTip
                          title=""
                          content="A TIN (Tax Identification Number) is a unique identifier that a country assigns to an individual. 
                          The TIN is used in administering tax laws. If you are eligible to receive a TIN from a country where you are a 
                          tax resident, you have 90 days to apply for one and must provide it to Meridian within 15 days of receiving your TIN."
                        />
                      </div>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.crsUsTin
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6",
                              style.TinErrorImage
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          autoComplete="off"
                          className={clsx(
                            "mt-2",
                            methods.formState?.errors?.crsUsTin?.message
                              ? "error"
                              : "",
                            style.occupationInput
                          )}
                          id="search"
                          type="text"
                          {...methods.register("crsUsTin")}
                        />
                      </div>
                      <span>
                        {methods.formState?.errors?.crsUsTin?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.crsUsTin?.message}
                          </span>
                        )}
                      </span>
                      <span>
                        {methods.formState?.errors?.crsUsTin?.type == "ssnValidator" && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            Enter a valid TIN. The format for TIN should be ###-##-####.
                          </span>
                        )}
                      </span>


                    </div>
                  )}


                  <div className="flex mt-5">
                    <span className="mr-1">
                      Are you a tax resident of a jurisdiction other than Canada
                      or the United States?
                      <span>
                        {methods.formState?.errors?.taxNotCanadaNotUS
                          ?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                              {
                                methods.formState?.errors?.taxNotCanadaNotUS
                                  ?.message
                              }
                            </span>
                          )}
                        {methods.formState?.errors?.residencyError?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.residencyError?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-2">
                      <LegalToolTip
                        title=""
                        content="Canadian FIs are required under Part XVIII and
                        Part XIX of the Income Tax Act to collect
                        information to determine if your account is
                        reportable to CRA. The CRA may share this
                        information with the government of a foreign
                        country that you are resident of for tax purposes.
                        Each account holder must complete this
                        information. Further help with tax residency is
                        available on CRA website “Determining an
                        Individual’s Residence Status” or by calling
                        1-800-959-8281."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="taxNotCanadaNotUSYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          checked={taxResidentOtherSelected === "yes"}
                          {...methods.register("taxNotCanadaNotUS", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentOtherHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="taxNotCanadaNotUSYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="taxNotCanadaNotUSYNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          checked={taxResidentOtherSelected === "no"}
                          {...methods.register("taxNotCanadaNotUS", {
                            required: "Choose an option that applies to you",
                            onChange: taxResidentOtherHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="taxNotCanadaNotUSYNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  {taxResidentOtherSelected == "yes" && (
                    <div className="mt-5">
                      <div className="flex md:flex-row flex-col w-max">
                        <div className="mt-5">
                          <label htmlFor="crsCountry">
                            Country of tax residency
                          </label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.crsCountry
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6",
                                  style.selectErrorImage
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <select
                              defaultValue=""
                              {...methods.register("crsCountry")}
                              className={clsx(
                                style.customDropdown,
                                methods.formState?.errors?.crsCountry?.message
                                  ? "error"
                                  : "",
                                "pt-2.5 mt-3"
                              )}
                              id="natureOfBanking"
                            >
                              <option hidden value="">
                                Choose...
                              </option>
                              {getCountry}
                            </select>
                          </div>
                          {methods.formState?.errors?.crsCountry?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState?.errors?.crsCountry?.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className={clsx(
                          "flex mt-5",
                          style.Tin
                        )}
                      >
                        <span className="mr-1">TIN</span>
                        <span>
                          <LegalToolTip
                            title=""
                            content="A TIN (Tax Identification Number) is a unique
                        identifier that a country assigns to an
                        individual. The TIN is used in administering
                        tax laws. If you are eligible to receive a TIN
                        from a country where you are a tax resident,
                        you have 90 days to apply for one and must
                        provide it to Meridian within 15 days of
                        receiving your TIN."
                          />
                        </span>
                      </div>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.crsTin ? "" : "hidden",
                              "w-4 h-4 absolute top-6",
                              style.TinErrorImage
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          autoComplete="off"
                          className={clsx(
                            "mt-2",
                            methods.formState?.errors?.crsTin?.message
                              ? "error"
                              : "",
                            style.occupationInput
                          )}
                          id="search"
                          type="text"
                          maxLength={30}
                          {...methods.register("crsTin", {
                            validate: {
                              tinValidator: (v) => isValidTin(v) == true,
                            },
                            required: "TIN is required"
                          })}
                        />
                      </div>
                      <span>
                        {methods.formState?.errors?.crsTin?.type == "tinValidator" && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            Enter a valid TIN
                          </span>
                        )}
                        {methods.formState?.errors?.crsTin?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.crsTin?.message}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {/* <div className="flex mt-5">
                    <span className="mr-1">
                      Are you or have you ever been a Politically exposed
                      Person, or related to a <br />
                      Politically exposed Person, or a close associate of a
                      Politically Exposed Person?
                      <span>
                        {methods.formState?.errors?.polExposed?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.polExposed?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-2 mt-2">
                      <LegalToolTip
                        title=""
                        content="Meridian is required by law to identify accounts
                        held by or on behalf of Politically Exposed
                        Persons, Heads of International Organizations and their Family Members and Close Associates. 
                        If you do or have ever held a public position like those listed or are closely related to such a person, please answer 'yes'.
                        A Politically Exposed Person (PEP) is an individual who holds or has held one of the following offices or positions in or on behalf of a country:
                        Head of state or head of government; Member of executive council of government;
                        Member of legislature;
                        Deputy minister or equivilent rank;
                        Ambassador or Attaché or counsellor of an ambassador;
                        Military officer with a rank of general of above;
                        President of a state-owned company;
                        President/Head of a state-owned bank;
                        Judge;
                        Leader or president of a political party represented in a legislature;
                        Mayor;
                        President/Head of an International Organization;
                        A person 'related' to a PEP is an individual who has one of the following relationships: 
                        Spouse or common-law partner; Child; Mother or Father; Mother-in-law or Father-in-law; Brother; sister; 
                        half-brother; half sister. Some close associates to a PEP include: Business partners; romantic relationships; 
                        serves on same board; prominent members of political party/union."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="polExposedYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          checked={pepPositionSelected === "yes"}
                          {...methods.register("polExposed", {
                            required: "Choose an option that applies to you",
                            onChange: pepPositionHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="polExposedYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="polExposedNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          checked={pepPositionSelected === "no"}
                          {...methods.register("polExposed", {
                            required: "Choose an option that applies to you",
                            onChange: pepPositionHandler,
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="polExposedNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  {pepPositionSelected == "yes" && (
                    <div>
                      <div className="flex md:flex-row flex-col w-max">
                        <div className="mt-5">
                          <label htmlFor="politicallyExposedType">
                            Who is the politically exposed person?
                          </label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors
                                    ?.politicallyExposedType
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6",
                                  style.selectErrorImage
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <select
                              defaultValue=""
                              {...methods.register("politicallyExposedType")}
                              className={clsx(
                                style.customDropdown,
                                methods.formState?.errors
                                  ?.politicallyExposedType?.message
                                  ? "error"
                                  : "",
                                "pt-2.5 mt-3"
                              )}
                              id="politicallyExposedType"
                            >
                              <option hidden value="">
                                Choose...
                              </option>
                              <option value="self">Self</option>
                              <option value="relative">Relative</option>
                            </select>
                          </div>
                          {methods.formState?.errors?.politicallyExposedType
                            ?.message && (
                              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                {
                                  methods.formState?.errors
                                    ?.politicallyExposedType?.message
                                }
                              </span>
                            )}
                        </div>
                      </div>
                      <div className="flex md:flex-row flex-col w-max">
                        <div className="mt-5">
                          <label htmlFor="positionHeld">
                            Position or office held
                          </label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.positionHeld
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6",
                                  style.selectErrorImage
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <select
                              defaultValue=""
                              {...methods.register("positionHeld")}
                              className={clsx(
                                style.customDropdown,
                                methods.formState?.errors?.positionHeld?.message
                                  ? "error"
                                  : "",
                                "pt-2.5 mt-3"
                              )}
                              id="positionHeld"
                            >
                              <option hidden value="">
                                Choose...
                              </option>
                              {pepPositionList()}
                            </select>
                          </div>
                          {methods.formState?.errors?.positionHeld?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState?.errors?.positionHeld?.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex md:flex-row flex-col w-max">
                        <div className="mt-5">
                          <label htmlFor="countryPositionHeld">
                            Country of Position or Office Held
                          </label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.countryPositionHeld
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6",
                                  style.selectErrorImage
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <select
                              defaultValue=""
                              {...methods.register("countryPositionHeld")}
                              className={clsx(
                                style.customDropdown,
                                methods.formState?.errors?.countryPositionHeld
                                  ?.message
                                  ? "error"
                                  : "",
                                "pt-2.5 mt-3"
                              )}
                              id="countryPositionHeld"
                            >
                              <option hidden value="">
                                Choose...
                              </option>
                              {getCountryInclNA}
                            </select>
                          </div>
                          {methods.formState?.errors?.countryPositionHeld
                            ?.message && (
                              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                {
                                  methods.formState?.errors?.countryPositionHeld
                                    ?.message
                                }
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  )} */}
                  <div
                    className={clsx(
                      "flex mt-5",
                      style.EmpCanada
                    )}
                  >
                    <span className="mr-1">
                      Are you employed in Canada?
                      <span>
                        {methods.formState?.errors?.ontarioWork?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.ontarioWork?.message}
                          </span>
                        )}
                        {methods.formState?.errors?.employmentError?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.employmentError?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-2">
                      <LegalToolTip
                        title=""
                        content="You are eligible to open an account if you either live or work in Canada."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="ontarioWorkYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          {...methods.register("ontarioWork", {
                            required: "Choose an option that applies to you",
                          })}
                          onChange={(e => {
                            methods.clearErrors("employmentError");
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="ontarioWorkYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="ontarioWorkNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          {...methods.register("ontarioWork", {
                            required: "Choose an option that applies to you",
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="ontarioWorkNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  {/* <div className="mt-5">
                    <label htmlFor="occupation">Search for your occupation</label>
                    <div className="flex relative mt-3">
                      <span>
                        <SearchableDropdown
                          options={sessionData.occupations}
                          label="name"
                          id="id"
                          selectedVal={occupationValue}
                          handleChange={(val: any) => {
                            let category = sessionData.occupations?.find((x) => x.name == val)?.category;
                            setOccupationCategory(category ? category : "");
                            setOccupationValue(val);
                          }}
                          customStyle={{}}
                          disableForm={false}
                          fetching={false}
                          displayError={false}
                        />
                      </span>
                    </div>
                    <small
                      className={clsx(
                        methods.formState?.errors?.occupation ? "hidden" : "",
                        "text-gray-400"
                      )}
                    >
                      <b>Example</b>: Financial Planner, Software Designer,
                      Marketing <br />
                      Manager
                    </small>
                    {methods.formState?.errors?.occupation && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.occupation.message}
                      </span>
                    )}
                  </div> */}
                  <div
                    className={clsx(
                      "flex mt-3",
                      style.busOwner
                    )}
                  >
                    <span className="mr-1">
                      Are you a business owner/operator?
                      <span>
                        {methods.formState?.errors?.ownerOperator?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.ownerOperator?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-2">
                      <LegalToolTip
                        title=""
                        content="Anti-money laundering legislation requires us to
                          collect this information."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="ownerOperatorYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          {...methods.register("ownerOperator", {
                            required: "Choose an option that applies to you",
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="ownerOperatorYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="ownerOperatorNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          {...methods.register("ownerOperator", {
                            required: "Choose an option that applies to you",
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="ownerOperatorNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div>
                  {/* <div className="flex md:flex-row flex-col w-max">
                    <div className="mt-5">
                      <label htmlFor="natureOfBanking">
                        Intended relationship
                      </label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.natureOfBanking
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6",
                              style.selectErrorImage
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          defaultValue=""
                          {...methods.register("natureOfBanking", {
                            required: "Select your intended relationship",
                          })}
                          className={clsx(
                            style.customDropdown,
                            methods.formState?.errors?.natureOfBanking?.message
                              ? "error"
                              : "",
                            "pt-2.5 mt-3"
                          )}
                          id="natureOfBanking"
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="personal" style={{ fontFamily: 'SofiaPro' }}>Personal</option>
                          <option value="personalBusiness">
                            Personal and Business
                          </option>
                        </select>
                      </div>
                      {methods.formState?.errors?.natureOfBanking?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState?.errors?.natureOfBanking?.message}
                        </span>
                      )}
                    </div>
                  </div> */}
                  {/* <div
                    className={clsx(
                      "flex mt-5",
                      style.meridianRelation
                    )}
                  >
                    <span className="mr-1">
                      Will Meridian be the only financial institution you bank
                      with?
                      <span>
                        {methods.formState?.errors?.meridianOnly?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1">
                            {methods.formState?.errors?.meridianOnly?.message}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ml-2">
                      <LegalToolTip
                        title=""
                        content="Anti-money laundering legislation requires us to
                          collect this information."
                      />
                    </span>
                  </div>
                  <div className="mt-3">
                    <ul className="flex">
                      <li className="relative mr-5">
                        <input
                          id="meridianOnlyYes"
                          type="radio"
                          value="yes"
                          className="sr-only peer"
                          {...methods.register("meridianOnly", {
                            required: "Choose an option that applies to you",
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100  text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="meridianOnlyYes"
                        >
                          <p className="p-2 brand-blue-text">Yes</p>
                        </label>
                      </li>
                      <li className="relative">
                        <input
                          id="meridianOnlyNo"
                          type="radio"
                          value="no"
                          className="sr-only peer"
                          {...methods.register("meridianOnly", {
                            required: "Choose an option that applies to you",
                          })}
                        />
                        <label
                          className={clsx(
                            style.smTypeLabel,
                            "cursor-pointer focus:outline-none hover:bg-gray-100 text-sm text-center peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent"
                          )}
                          htmlFor="meridianOnlyNo"
                        >
                          <p className="p-2 brand-blue-text">No</p>
                        </label>
                      </li>
                    </ul>
                  </div> */}
                  <div className="flex max-sm:hidden">
                    <div className="mb-5 mr-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-169 sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        {applicant.isProspect ? "Continue" : "Submit"}
                      </button>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        {applicant.isProspect ? "Continue" : "Submit"}
                      </button>
                    </div>
                    <div className="mb-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-full sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </SlideTransition>
  );
}